/**
 * File: bfwt-popups.js (Vanilla JS - Complete & Modular Version)
 * Purpose: A library for creating and managing all popups (modals).
 */
(function () {
  'use strict';

  // QR Code library removed - using static SVG per user request

  // Button class strings — mirror the canonical PHP ButtonShortcode::STYLES
  // so popup CTAs share the exact look used everywhere else on the site.
  // Kept in sync manually; the popup system is JS-only and can't import PHP.
  const BTN_BASE =
    'tw-inline-flex tw-items-center tw-justify-center tw-gap-2.5 tw-font-sans tw-font-semibold tw-tracking-[var(--ls-h5)] tw-no-underline tw-leading-none tw-rounded-card tw-cursor-pointer tw-select-none tw-transition-all tw-duration-300 tw-text-sm max-md:tw-text-xs tw-py-3.5 tw-px-7 max-md:tw-py-2.5 max-md:tw-px-5 tw-border-0';
  const BTN_PRIMARY =
    BTN_BASE + ' tw-bg-accent tw-text-white hover:tw-bg-[#03724e]';
  const BTN_OUTLINE =
    BTN_BASE.replace(
      'tw-border-0',
      'tw-border tw-border-solid tw-border-ink/30'
    ) +
    ' tw-text-ink tw-bg-transparent hover:tw-bg-ink/10 hover:tw-border-ink/40';

  // Universal close button — exact mirror of the canonical floating-header
  // close (CoreLayout/Templates/Partials/Header.php:44–47) so X buttons
  // across modals, sheets, and the header read as one component.
  const BTN_CLOSE =
    'tw-flex-none tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full tw-border tw-border-line/40 tw-bg-ink/[0.06] tw-text-secondary tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-bg-accent hover:tw-text-night hover:tw-border-accent hover:tw-rotate-90';

  const OPT_CLS =
    'bfwt-feedback-option tw-flex tw-items-center tw-gap-3 tw-border tw-border-solid tw-border-line/40 tw-rounded-card tw-py-3.5 tw-px-4 tw-text-sm tw-cursor-pointer tw-bg-paper tw-text-ink tw-transition-all tw-duration-200 hover:tw-border-accent/40 hover:tw-bg-accent/[0.02] tw-select-none';
  const RADIO_CLS =
    'bfwt-radio-circle tw-w-4 tw-h-4 tw-rounded-full tw-border-2 tw-border-solid tw-border-line/60 tw-relative tw-flex-shrink-0 tw-transition-colors tw-duration-200';

  // ─── Toast — universal in-place notification (success / error / warning /
  // info). Replaces the old per-widget inline error spans. Pure Tailwind
  // chrome, stacks bottom-right (mobile bottom-full-width). z-toast=10200.
  // Use: BFWT_Toast.success('Saved!') / .error('Could not connect.', { title: 'Network error' })
  const TOAST_TYPES = {
    success: { icon: 'check_circle', tone: 'tw-text-positive' },
    error: { icon: 'error', tone: 'tw-text-negative' },
    warning: { icon: 'warning', tone: 'tw-text-caution' },
    info: { icon: 'info', tone: 'tw-text-accent' },
  };

  window.BFWT_Toast = {
    _host: null,
    _ensureHost() {
      if (this._host && document.body.contains(this._host)) return this._host;
      const host = document.createElement('div');
      // Fixed below the sticky header, centred. z-toast (10200) clears header (10003) and sheets (10002).
      host.className =
        'bfwt-toast-host tw-fixed tw-z-toast tw-top-[var(--header-stack,5rem)] tw-left-1/2 -tw-translate-x-1/2 tw-flex tw-flex-col tw-items-stretch tw-gap-2 tw-pointer-events-none tw-w-full tw-max-w-lg tw-px-4';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
      this._host = host;
      return host;
    },
    show(message, opts = {}) {
      const { type = 'info', title = '', duration = 6000, action = null, icon = '' } = opts;
      const meta = TOAST_TYPES[type] || TOAST_TYPES.info;
      const iconName = icon || meta.icon;
      const host = this._ensureHost();

      const toast = document.createElement('div');
      const alignClass = title ? 'tw-items-start' : 'tw-items-center';
      const iconMt = title ? 'tw-mt-0.5' : '';
      
      let actionBtnHtml = '';
      if (action && action.label) {
        actionBtnHtml = `<a href="${action.url || '#'}" class="bfwt-toast-action tw-flex-none tw-text-xs tw-font-heading tw-font-bold tw-px-3.5 tw-py-1.5 tw-rounded-full tw-bg-accent/15 hover:tw-bg-accent tw-text-accent hover:tw-text-cream tw-border tw-border-accent/30 tw-transition-all tw-duration-200 tw-no-underline">${action.label}</a>`;
      }

      toast.className = `bfwt-toast tw-pointer-events-auto tw-flex ${alignClass} tw-gap-3.5 tw-py-3.5 tw-px-4 sm:tw-px-5 tw-rounded-2xl tw-bg-paper/92 dark:tw-bg-[#16191E]/92 tw-backdrop-blur-2xl tw-backdrop-saturate-[180%] tw-border tw-border-line/70 dark:tw-border-white/[0.14] tw-shadow-[0_16px_40px_rgba(0,0,0,0.28)] -tw-translate-y-3 tw-opacity-0 tw-transition-all tw-duration-300`;
      toast.innerHTML = `
                <span class="tw-w-8 tw-h-8 tw-rounded-xl tw-bg-ink/[0.05] dark:tw-bg-white/[0.06] tw-border tw-border-line/50 dark:tw-border-white/[0.08] tw-flex tw-items-center tw-justify-center tw-flex-none ${iconMt} ${meta.tone}">
                    <span class="material-symbols-rounded tw-text-[18px] tw-leading-none" aria-hidden="true">${iconName}</span>
                </span>
                <div class="tw-flex-1 tw-min-w-0 tw-text-[13.5px] tw-leading-snug">
                    ${title ? `<div class="tw-font-heading tw-font-bold tw-text-xs tw-uppercase tw-tracking-wider tw-text-ink tw-mb-0.5">${title}</div>` : ''}
                    <div class="tw-font-medium tw-text-ink/90 dark:tw-text-white/90">${message}</div>
                </div>
                ${actionBtnHtml}
                <button type="button" class="bfwt-toast-close tw-flex-none tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-secondary hover:tw-text-ink tw-bg-ink/[0.05] hover:tw-bg-ink/[0.1] dark:tw-bg-white/[0.06] dark:hover:tw-bg-white/[0.12] tw-border tw-border-line/50 dark:tw-border-white/[0.1] tw-transition-all tw-duration-200 tw-cursor-pointer" aria-label="Dismiss">
                    <span class="material-symbols-rounded tw-text-[14px] tw-leading-none">close</span>
                </button>
            `;
      host.appendChild(toast);

      let dismissed = false;
      const close = () => {
        if (dismissed) return;
        dismissed = true;
        toast.classList.add('tw-opacity-0', '-tw-translate-y-3');
        setTimeout(() => toast.remove(), 300);
      };

      toast.querySelector('.bfwt-toast-close').addEventListener('click', close);

      // Slide in from above on next frame so the transition fires.
      requestAnimationFrame(() => {
        toast.classList.remove('tw-opacity-0', '-tw-translate-y-3');
      });

      if (duration > 0) setTimeout(close, duration);
      return { close };
    },
    success(msg, opts) {
      return this.show(msg, { ...opts, type: 'success' });
    },
    error(msg, opts) {
      return this.show(msg, { ...opts, type: 'error' });
    },
    warning(msg, opts) {
      return this.show(msg, { ...opts, type: 'warning' });
    },
    info(msg, opts) {
      return this.show(msg, { ...opts, type: 'info' });
    },
  };

  // Tüm popup mantığını yönetecek olan global nesneyi oluşturuyoruz.
  window.BFWT_Popups = {
    // --- HELPER FUNCTIONS ---
    getRandomCode(promoString) {
      if (!promoString) return '';
      const codes = promoString
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      if (codes.length === 0) return '';
      return codes[0];
    },

    /** Parse **...** in modal titles to <span class="u-text-neon">...</span> */
    parseNeonTitle(str) {
      if (typeof str !== 'string') return '';
      return str.replace(
        /\*\*([\s\S]*?)\*\*/g,
        '<span class="u-text-neon">$1</span>'
      );
    },

    escapeHtml(str) {
      return String(str === null || str === undefined ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    buildTimelineHtml(hasCode) {
      const steps = hasCode
        ? [
            {
              icon: 'content_copy',
              title: 'Copy your code',
              text: 'This copies the code automatically and directs you to the partner platform.',
            },
            {
              icon: 'touch_app',
              title: 'Take action & proceed',
              text: 'Head to checkout, complete your sign-up, or prepare your order.',
            },
            {
              icon: 'redeem',
              title: 'Apply the code',
              text: 'Enter or paste the code when prompted during checkout, sign-up, or payment.',
            },
            {
              icon: 'celebration',
              title: 'Enjoy your perk!',
              text: 'Your reward, bonus credit, or verified discount is successfully activated.',
            },
          ]
        : [
            {
              icon: 'auto_awesome',
              title: 'Click our button',
              text: 'Click "Claim Deal" to open the partner landing page.',
            },
            {
              icon: 'verified_user',
              title: 'Offer is activated',
              text: 'Your exclusive perk or partner privilege is automatically unlocked.',
            },
            {
              icon: 'celebration',
              title: 'Complete & enjoy',
              text: 'The verified benefit is applied seamlessly as you complete the process.',
            },
          ];

      let html = '<div class="bfwt-redeem-timeline">';
      steps.forEach((step) => {
        html += `
          <div class="bfwt-redeem-step-wrapper">
            <div class="bfwt-redeem-step-icon">
              <span class="material-symbols-rounded tw-text-[14px]">${step.icon}</span>
            </div>
            <div class="tw-pl-2">
              <h5 class="tw-font-heading tw-font-bold tw-text-sm tw-text-ink tw-m-0 tw-mb-1">${step.title}</h5>
              <p class="tw-text-secondary tw-text-xs tw-m-0 tw-leading-relaxed">${step.text}</p>
            </div>
          </div>`;
      });
      html += '</div>';
      return html;
    },

    async copyToClipboard(text) {
      if (!text) return { success: false, message: 'No text to copy.' };
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          if (window.BFWT_Accessibility) {
            window.BFWT_Accessibility.announce('Copied to clipboard');
          }
          return { success: true, message: 'Copied!' };
        } catch {
          return this._fallbackCopy(text);
        }
      }
      return this._fallbackCopy(text);
    },

    _fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch {}
      document.body.removeChild(textArea);

      // A11y Announcement
      if (window.BFWT_Accessibility && success) {
        window.BFWT_Accessibility.announce('Coupon code copied: ' + text);
      }

      return { success, message: success ? 'Copied!' : 'Copy failed.' };
    },

    openDeepLink(brandSlug, webUrl) {
      const isAndroid = /android/i.test(navigator.userAgent);

      // Android: Intent URI — Chrome handles this natively for installed apps.
      // S.browser_fallback_url provides graceful web fallback if app is not installed.
      // Note: window.location.href does NOT trigger App Links for same-origin
      // navigations on Chrome Android, so we use an <a> click with intent:// instead.
      if (isAndroid) {
        const intentUrl = `intent://brand/${encodeURIComponent(brandSlug)}#Intent;scheme=budgetfitter;package=co.uk.budgetfitter.app;S.browser_fallback_url=${encodeURIComponent(webUrl)};end`;
        const a = document.createElement('a');
        a.href = intentUrl;
        a.click();
        return;
      }

      // iOS: Use custom scheme — Safari handles budgetfitter:// reliably
      const appUrl = `budgetfitter://brand/${brandSlug}`;
      let fallbackTimer = null;
      let appOpened = false;

      const cancelFallback = () => {
        appOpened = true;
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        cleanup();
      };

      const cleanup = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', cancelFallback);
        window.removeEventListener('blur', cancelFallback);
      };

      const onVisibilityChange = () => {
        if (document.hidden) {
          cancelFallback();
        }
      };

      // Listen for signals that the app opened (browser goes to background)
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pagehide', cancelFallback);
      window.addEventListener('blur', cancelFallback);

      // Attempt to open app via custom scheme
      window.location.href = appUrl;

      // Fallback: only redirect to web if app didn't open
      fallbackTimer = setTimeout(() => {
        cleanup();
        if (!appOpened && !document.hidden) {
          window.location.href = webUrl;
        }
      }, 2500);
    },

    // --- MODAL MANAGEMENT ---
    showAndHandleModal(
      modalHtml,
      modalSelector,
      trackingTitle,
      skipHistoryPush = false
    ) {
      if (document.querySelector(modalSelector)) return;

      const triggerElement = document.activeElement;
      document.body.insertAdjacentHTML('beforeend', modalHtml);
      document.body.classList.add('bfwt-popup-is-open');

      const modal = document.querySelector(modalSelector);
      const dealId = modal.dataset.dealId;

      if (dealId && !skipHistoryPush) {
        try {
          const newUrl = new URL(window.location.href);
          if (newUrl.searchParams.get('deal') !== String(dealId)) {
            newUrl.searchParams.set('deal', dealId);
            window.history.pushState({ dealId }, '', newUrl.toString());
          }
        } catch {}
      }

      modal.closeModal = () => {
        if (!modal || !modal.classList.contains('show')) return;

        if (window.BFWT_Accessibility)
          {window.BFWT_Accessibility.removeModalAccessibility();}

        // Only handle history if this modal pushed history (i.e., not skipHistoryPush)
        if (!skipHistoryPush) {
          try {
            const currentUrl = new URL(window.location.href);
            if (currentUrl.searchParams.has('deal')) {
              if (window.history.state && window.history.state.dealId) {
                window.history.back();
              } else {
                currentUrl.searchParams.delete('deal');
                window.history.replaceState({}, '', currentUrl.toString());
              }
            }
          } catch {}
        }

        modal.classList.remove('show');
        setTimeout(() => {
          modal.remove();
          if (
            document.querySelectorAll('.bfwt-popup-overlay.show').length === 0
          ) {
            document.body.classList.remove('bfwt-popup-is-open');
            window.onpopstate = null;
          }
        }, 300);
      };

      modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target.closest('.bfwt-popup-close')) {
          modal.closeModal();
        }
      });

      window.onpopstate = () => {
        const currentModal = document.querySelector('.bfwt-popup-overlay.show');
        if (currentModal && typeof currentModal.closeModal === 'function') {
          currentModal.closeModal();
        }
      };

      if (typeof window.bfTrack === 'function') {
        window.bfTrack('modal_open', {
          event_category: 'modal',
          modal_title: trackingTitle,
          modal_id: modal.id || '',
          deal_id: modal.dataset.dealId || '',
        });
      }

      setTimeout(() => {
        modal.classList.add('show');
        if (window.BFWT_Accessibility)
          {window.BFWT_Accessibility.initModalAccessibility(
            modal,
            triggerElement
          );}
      }, 10);

      return modal;
    },

    // --- UNIFIED MODAL CREATION HELPER ---
    createModal(options) {
      const {
        id,
        className = '',
        title,
        content,
        footer = '',
        dealId = '',
        extraAttrs = '',
      } = options;
      const footerHtml = footer
        ? `<div class="bfwt-popup-footer tw-pt-5 tw-mt-5 tw-border-t tw-border-line/40 tw-text-center tw-flex-shrink-0">${footer}</div>`
        : '';
      const titleHtml = this.parseNeonTitle(title || '');

      // Box chrome shares the floating-header / sheet vocabulary —
      // solid paper bg, neutral line/40 border, card radius, deep
      // elevated shadow. No translucency, no green accent border.
      // z-modal (10100) sits above z-header (10003) so the close button
      // is reachable even when the sticky header is visible on mobile.
      return `
                <div id="${id}" class="bfwt-popup-overlay ${className} tw-fixed tw-inset-0 tw-items-center tw-justify-center tw-z-modal" data-deal-id="${dealId}" ${extraAttrs}>
                    <div class="bfwt-popup-box tw-relative tw-w-[90%] tw-max-w-[50rem] tw-box-border tw-flex tw-flex-col tw-max-h-[90vh] tw-rounded-card tw-bg-paper tw-border tw-border-line/40 tw-shadow-2xl tw-p-5 md:tw-p-8">
                        <div class="bfwt-popup-header tw-flex tw-justify-between tw-items-center tw-pb-3.5 tw-mb-3.5 tw-border-b tw-border-line/40 tw-flex-shrink-0">
                            <h2 class="bfwt-popup-title tw-m-0 tw-text-xl max-md:tw-text-lg tw-leading-tight tw-text-ink tw-flex-grow tw-pr-5" id="bfwt-popup-title-${dealId}">${titleHtml}</h2>
                            <button class="bfwt-popup-close ${BTN_CLOSE}" aria-label="Close Popup"><span class="material-symbols-rounded">close</span></button>
                        </div>
                        <div class="bfwt-popup-content-wrapper tw-flex-1 tw-overflow-y-auto tw-p-3.5 [-webkit-overflow-scrolling:touch]">
                            ${content}
                        </div>
                        ${footerHtml}
                    </div>
                </div>`;
    },

    // --- POPUP CREATION (FULL & ORIGINAL VERSIONS) ---
    createAndShowAppOnlyPopup(dealData) {
      const dealid = dealData.dealid;
      const brandName =
        dealData['brand-name'] || dealData.brandname || 'Exclusive Brand';
      const brandSlug = dealData['brand-slug'] || dealData.brandslug;
      const appDownloadLink = 'https://budgetfitter.uk/download/';
      const deepLink = `https://budgetfitter.uk/brand/${brandSlug}/`;
      // Static QR Code
      const qrUrl = 'https://budgetfitter.uk/wp-content/uploads/2026/02/qr.svg';

      const content = `
                <div class="bfwt-app-only-content tw-flex tw-flex-col tw-items-center tw-gap-6 tw-w-full">
                    <div class="tw-bg-gradient-to-tr tw-from-accent/[0.03] tw-via-surface tw-to-accent/[0.08] tw-border tw-border-line/30 tw-rounded-card tw-p-8 tw-w-full tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-5 tw-shadow-inner tw-relative tw-overflow-hidden">
                        <div class="tw-absolute tw-w-64 tw-h-64 tw-bg-accent/5 tw-rounded-full tw-blur-3xl tw--top-20 tw--left-20 tw-pointer-events-none"></div>
                        <div class="tw-relative tw-z-[1] tw-w-16 tw-h-16 tw-rounded-2xl tw-bg-accent tw-text-white tw-flex tw-items-center tw-justify-center tw-shadow-[0_8px_20px_rgba(4,135,93,0.3)]">
                            <span class="material-symbols-rounded tw-text-3xl">phone_iphone</span>
                        </div>
                        <div class="tw-relative tw-z-[1] tw-max-w-md">
                            <h4 class="tw-font-heading tw-font-bold tw-text-base md:tw-text-lg tw-text-ink tw-m-0 tw-mb-2">App Exclusive Offer</h4>
                            <p class="bfwt-app-only-text tw-text-sm tw-leading-relaxed tw-text-secondary tw-m-0">This exclusive discount is available only in the BudgetFitter app.</p>
                        </div>
                        
                        <div class="bfwt-app-qr-code only-desktop tw-flex tw-flex-col tw-items-center tw-gap-3 tw-mt-2 max-md:tw-hidden tw-relative tw-z-[1]">
                            <div class="tw-bg-paper tw-p-4 tw-rounded-2xl tw-border tw-border-line/40 tw-animate-qr-glow tw-transition-all tw-duration-300 tw-relative">
                                <img src="${qrUrl}" alt="Scan to Download" loading="lazy" class="tw-block tw-max-w-[10rem] tw-w-full tw-h-auto">
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-1.5">
                                <span class="material-symbols-rounded tw-text-[12px] tw-text-accent">qr_code_scanner</span>
                                <span class="tw-text-[0.7rem] tw-text-secondary tw-font-heading tw-font-bold tw-uppercase tw-tracking-widest">Scan to download</span>
                            </div>
                        </div>
                    </div>
                    <p class="ally-notice tw-italic tw-text-[0.75rem] tw-text-tertiary tw-text-center tw-m-0 tw-max-w-[90%]">Your Ally would love for this to be simpler, but we have to play by the brand's rules!</p>
                </div>
            `;

      const footer = `
                <div class="bfwt-app-only-actions tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <a href="${appDownloadLink}" class="${BTN_PRIMARY} tw-shadow-[0_4px_14px_rgba(4,135,93,0.3)] hover:tw-shadow-[0_6px_20px_rgba(4,135,93,0.4)] bfwt-popup-cta" target="_blank" rel="noopener">
                        <span class="material-symbols-rounded">phone_iphone</span> Download App <span class="material-symbols-rounded">arrow_forward</span>
                    </a>
                    <button class="${BTN_OUTLINE} hover:tw-bg-ink/5 hover:tw-border-ink/50 bfwt-open-in-app-btn">
                        Already have the app? Open Deal
                    </button>
                </div>
            `;

      const modalHtml = this.createModal({
        id: `app-only-popup-${dealid}`,
        className: 'bfwt-app-only-modal',
        title: `Get the ${brandName} Code`,
        content: content,
        footer: footer,
        dealId: dealid,
      });

      const modal = this.showAndHandleModal(
        modalHtml,
        `#app-only-popup-${dealid}`,
        `AppOnly - ${brandName}`
      );

      // "Already have app?" button - try deep link then web fallback
      modal
        .querySelector('.bfwt-open-in-app-btn')
        .addEventListener('click', (e) => {
          e.preventDefault();
          this.openDeepLink(brandSlug, deepLink);
        });
    },

    createAndShowWatchlistPopup(dealData) {
      const dealid = dealData.dealid;
      const brandName =
        dealData['brand-name'] || dealData.brandname || 'Exclusive Brand';
      const brandSlug = dealData['brand-slug'] || dealData.brandslug;
      const appDownloadLink = 'https://budgetfitter.uk/download/';
      const deepLink = `https://budgetfitter.uk/brand/${brandSlug}/`;
      // Static QR Code
      const qrUrl = 'https://budgetfitter.uk/wp-content/uploads/2026/02/qr.svg';

      const content = `
                <div class="bfwt-app-only-content tw-flex tw-flex-col tw-items-center tw-gap-6 tw-w-full">
                    <div class="tw-bg-gradient-to-tr tw-from-accent/[0.03] tw-via-surface tw-to-accent/[0.08] tw-border tw-border-line/30 tw-rounded-card tw-p-8 tw-w-full tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-5 tw-shadow-inner tw-relative tw-overflow-hidden">
                        <div class="tw-absolute tw-w-64 tw-h-64 tw-bg-accent/5 tw-rounded-full tw-blur-3xl tw--top-20 tw--left-20 tw-pointer-events-none"></div>
                        <div class="tw-relative tw-z-[1] tw-w-16 tw-h-16 tw-rounded-2xl tw-bg-accent tw-text-white tw-flex tw-items-center tw-justify-center tw-shadow-[0_8px_20px_rgba(4,135,93,0.3)]">
                            <span class="material-symbols-rounded tw-text-3xl">notifications_active</span>
                        </div>
                        <div class="tw-relative tw-z-[1] tw-max-w-md">
                            <h4 class="tw-font-heading tw-font-bold tw-text-base md:tw-text-lg tw-text-ink tw-m-0 tw-mb-2">Get Price Alerts</h4>
                            <p class="bfwt-app-only-text tw-text-sm tw-leading-relaxed tw-text-secondary tw-m-0">Never miss a deal again! Get instant notifications for <strong>${brandName}</strong> with the free BudgetFitter App.</p>
                        </div>
                        
                        <div class="bfwt-app-qr-code only-desktop tw-flex tw-flex-col tw-items-center tw-gap-3 tw-mt-2 max-md:tw-hidden tw-relative tw-z-[1]">
                            <div class="tw-bg-paper tw-p-4 tw-rounded-2xl tw-border tw-border-line/40 tw-animate-qr-glow tw-transition-all tw-duration-300 tw-relative">
                                <img src="${qrUrl}" alt="Scan to Download" loading="lazy" class="tw-block tw-max-w-[10rem] tw-w-full tw-h-auto">
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-1.5">
                                <span class="material-symbols-rounded tw-text-[12px] tw-text-accent">qr_code_scanner</span>
                                <span class="tw-text-[0.7rem] tw-text-secondary tw-font-heading tw-font-bold tw-uppercase tw-tracking-widest">Scan to download</span>
                            </div>
                        </div>
                    </div>
                </div>
            `;

      const footer = `
                <div class="bfwt-app-only-actions tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <a href="${appDownloadLink}" class="${BTN_PRIMARY} tw-shadow-[0_4px_14px_rgba(4,135,93,0.3)] hover:tw-shadow-[0_6px_20px_rgba(4,135,93,0.4)] bfwt-popup-cta" target="_blank" rel="noopener">
                        <span class="material-symbols-rounded">phone_iphone</span> Download App <span class="material-symbols-rounded">arrow_forward</span>
                    </a>
                    <button class="${BTN_OUTLINE} hover:tw-bg-ink/5 hover:tw-border-ink/50 bfwt-open-in-app-btn">
                        Already have the app? Open
                    </button>
                </div>
            `;

      const modalHtml = this.createModal({
        id: `watchlist-popup-${dealid}`,
        className: 'bfwt-app-only-modal',
        title: `Watch ${brandName}`,
        content: content,
        footer: footer,
        dealId: dealid,
      });

      const modal = this.showAndHandleModal(
        modalHtml,
        `#watchlist-popup-${dealid}`,
        `Watchlist - ${brandName}`
      );

      modal
        .querySelector('.bfwt-open-in-app-btn')
        .addEventListener('click', (e) => {
          e.preventDefault();
          this.openDeepLink(brandSlug, deepLink);
        });
    },

    showDealModal(dealData, options = {}) {
      if (!dealData) return;
      const promoCode = dealData.promo || dealData.promoCode || '';
      const isCodeless = !(promoCode && promoCode.trim() !== '');
      this.renderDealModal(dealData, Object.assign({ isCodeless }, options));
    },

    createAndShowCouponModal(dealData, options = {}) {
      this.renderDealModal(dealData, Object.assign({ isCodeless: false }, options));
    },

    createAndShowCodelessModal(dealData, options = {}) {
      this.renderDealModal(dealData, Object.assign({ isCodeless: true }, options));
    },

    renderDealModal(dealData, options = {}) {
      const isCodeless = !!options.isCodeless;
      const dealId = dealData.dealid || dealData.dealId;
      const rawPromo = isCodeless ? '' : (dealData.promo || dealData.promoCode || '');
      const promoCode = this.getRandomCode(rawPromo);
      const brandName = (dealData.brandName || dealData['brand-name'] || dealData.brandname || '').trim();
      const brandLogoUrl = dealData.brandLogo || dealData.logo || dealData['brand-logo'] || '';
      const ctaLink = dealData.ctalink || dealData.ctaLink || dealData.affiliateLink || '#';

      const logoWhite = 'https://budgetfitter.uk/wp-content/uploads/2026/08/budgetfitter-logo-light.svg';
      const disclosureLink = window.bfwtTranslations?.bf_core_disclosure_link || '#';
      const t = (k, def) => (window.bfwtTranslations && window.bfwtTranslations[k]) || def;
      const disclaimerText = `We may earn a commission when you use our links to make a purchase. This does not affect the price you pay. All offers are subject to merchant's terms, which you should verify on their website. While we strive for accuracy, we cannot guarantee all deals are valid. <a href="${disclosureLink}" target="_blank" rel="noopener" class="tw-text-accent tw-font-semibold tw-underline">See full disclosure</a>.`;

      // Check for distinct non-generic short title to avoid awkward "Promo Code" duplication
      const rawShortTitle = (dealData.shorttitle || dealData.shortTitle || '').trim();
      const shortTitle = rawShortTitle || (isCodeless ? 'Special Offer' : 'Promo Code');
      const isGenericTitle = !rawShortTitle || /^(promo\s*code|special\s*offer|exclusive\s*deal|deal|coupon|discount)$/i.test(rawShortTitle);
      const formattedShortTitle = !isGenericTitle ? this.parseNeonTitle(rawShortTitle) : '';

      const headerBadge = isCodeless
        ? (brandName ? `${brandName} Special Deal` : 'Verified Deal')
        : (brandName ? `${brandName} Promo Code` : 'Promo Code');

      const modalIcon = isCodeless
        ? '<span class="material-symbols-rounded tw-text-3xl">verified</span>'
        : '<span class="material-symbols-rounded tw-text-3xl">check_circle</span>';

      const modalHeading = isCodeless
        ? t('no_code_heading', 'No Code Required!')
        : t('promo_copied_heading', 'Promo Code Copied!');

      const modalSubtitle = isCodeless
        ? `No code needed. Your verified perk will be automatically activated when you visit <strong>${brandName || 'the partner platform'}</strong>.`
        : `Apply this code on <strong>${brandName || 'the partner platform'}</strong> to claim your perk.`;

      const codeBoxHtml = !isCodeless ? `
                    <!-- Monospace Code Box -->
                    <div class="tw-w-full tw-bg-surface dark:tw-bg-white/5 tw-border tw-border-dashed tw-border-accent/40 tw-rounded-card tw-p-3 sm:tw-p-4 tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-3 tw-shadow-sm tw-overflow-hidden">
                        <span class="tw-font-mono tw-font-bold tw-text-lg sm:tw-text-2xl tw-text-accent tw-tracking-wider sm:tw-tracking-widest tw-truncate tw-select-all">${promoCode}</span>
                        <button type="button" class="${BTN_PRIMARY} bfwt-copy-modal-code-btn tw-shrink-0 tw-py-2 tw-px-3.5 sm:tw-px-5 tw-text-xs sm:tw-text-sm" data-code="${promoCode}">
                            <span class="material-symbols-rounded tw-text-base sm:tw-text-lg">content_copy</span> ${t('copy_btn', 'Copy')}
                        </button>
                    </div>
      ` : '';

      const ctaBtnClass = isCodeless ? `${BTN_PRIMARY} tw-py-3 tw-text-base` : `${BTN_OUTLINE} tw-py-2.5 sm:tw-py-3`;

      const overlayHtml = `
        <div id="coupon-popup-${dealId}" class="bfwt-popup-overlay bfwt-coupon-stacked-modal tw-fixed tw-inset-0 tw-z-modal tw-bg-ink/80 dark:tw-bg-black/85 tw-backdrop-blur-md tw-flex tw-items-center tw-justify-center tw-p-3 sm:tw-p-6 tw-overflow-y-auto" data-deal-id="${dealId}">
            <div class="tw-relative tw-w-full tw-max-w-[44rem] tw-box-border tw-flex tw-flex-col tw-gap-3.5 tw-my-auto">
                
                <!-- CARD 1: MAIN DEAL CARD -->
                <div class="tw-relative tw-w-full tw-rounded-card tw-bg-paper dark:tw-bg-[#1c1c1e] tw-overflow-hidden tw-border-2 tw-border-solid tw-border-accent tw-shadow-2xl tw-p-4 sm:tw-p-6 md:tw-p-8 tw-flex tw-flex-col tw-gap-4" style="border: 2px solid #04875D;">
                    <!-- Radial Glow Flourish -->
                    <div class="tw-absolute -tw-top-24 -tw-left-24 tw-w-64 tw-h-64 tw-rounded-full tw-bg-accent/[0.08] dark:tw-bg-accent/[0.15] tw-blur-3xl tw-pointer-events-none"></div>

                    <!-- Bottom Shimmer Line (Signature BudgetFitter flourish) -->
                    <div class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-[2px] tw-z-[3] tw-pointer-events-none [background:linear-gradient(90deg,transparent_0%,rgba(4,135,93,0.85)_50%,transparent_100%)]"></div>
                    <!-- Top Bar: Header Title + Close -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <div class="bfwt-badge bfwt-badge--unified bfwt-badge--theme tw-inline-flex tw-items-center tw-gap-1.5 !tw-py-1 !tw-px-3 !tw-text-[0.6875rem] sm:!tw-text-xs !tw-font-bold tw-uppercase tw-tracking-wide" style="--bfwt-theme-main-color:#04875D; --bfwt-theme-font-color:#ffffff;">
                            <span class="notranslate material-symbols-rounded tw-text-[1.15em] tw-text-accent tw-shrink-0" translate="no" aria-hidden="true">${isCodeless ? 'verified' : 'confirmation_number'}</span>
                            <span class="tw-truncate">${headerBadge}</span>
                        </div>
                        <button class="bfwt-popup-close ${BTN_CLOSE} tw-shrink-0" aria-label="Close Popup">
                            <span class="material-symbols-rounded">close</span>
                        </button>
                    </div>

                    <!-- Icon & Header Message -->
                    <div class="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-2">
                        <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-positive/10 tw-text-positive tw-flex tw-items-center tw-justify-center tw-shadow-sm">
                            ${modalIcon}
                        </div>
                        <h3 class="tw-font-heading tw-font-bold tw-text-xl sm:tw-text-2xl tw-text-ink dark:tw-text-white tw-m-0">${modalHeading}</h3>
                        <p class="tw-text-xs sm:tw-text-sm tw-text-secondary dark:tw-text-white/70 tw-m-0">${modalSubtitle}</p>
                    </div>

                    <!-- Deal Short Title (Only rendered when distinct non-generic offer details are provided) -->
                    ${formattedShortTitle ? `
                    <div class="tw-text-center tw-pt-1">
                        <p class="bfwt-short-title tw-font-heading tw-font-bold tw-text-base sm:tw-text-xl tw-text-ink dark:tw-text-white tw-m-0 tw-leading-snug">
                            ${formattedShortTitle}
                        </p>
                    </div>
                    ` : ''}

                    ${codeBoxHtml}

                    <!-- Continue to Store Link -->
                    ${ctaLink && ctaLink !== '#' ? `
                    <div class="tw-text-center">
                        <a href="${ctaLink}" target="_blank" rel="nofollow sponsored" class="${ctaBtnClass} tw-w-full tw-justify-center">
                            <span class="material-symbols-rounded">open_in_new</span> ${t('go_to_store', 'Continue to ' + (brandName || 'Store'))}
                        </a>
                    </div>
                    ` : ''}

                    <!-- Bottom Bar: Feedback Vote & Triggers -->
                    <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-w-full tw-pt-3.5 tw-border-t tw-border-line/20 dark:tw-border-white/10 tw-gap-3 sm:tw-gap-2 tw-text-xs">
                        <div class="bfwt-modal-feedback-box tw-flex tw-items-center tw-gap-2">
                            <span class="tw-text-tertiary dark:tw-text-white/60 tw-font-medium">${t('worked_question', 'Worked?')}</span>
                            <div class="bfwt-feedback-vote-buttons tw-flex tw-gap-1.5">
                                <button type="button" class="${BTN_OUTLINE} tw-py-1.5 tw-px-3 tw-text-xs tw-flex tw-items-center tw-gap-1 bfwt-modal-vote-yes" data-deal-id="${dealId}">
                                    <span class="material-symbols-rounded tw-text-sm tw-text-positive">thumb_up</span> ${t('yes', 'Yes')}
                                </button>
                                <button type="button" class="${BTN_OUTLINE} tw-py-1.5 tw-px-3 tw-text-xs tw-flex tw-items-center tw-gap-1 bfwt-modal-vote-no" data-deal-id="${dealId}">
                                    <span class="material-symbols-rounded tw-text-sm tw-text-negative">thumb_down</span> ${t('no', 'No')}
                                </button>
                            </div>
                            <span class="bfwt-modal-feedback-thankyou tw-hidden tw-text-positive tw-font-semibold">${t('feedback_saved', '✓ Feedback saved!')}</span>
                        </div>

                        <div class="tw-flex tw-items-center tw-justify-center tw-gap-3 sm:tw-gap-4 tw-flex-wrap">
                            <button type="button" class="bfwt-modal-open-info tw-text-secondary dark:tw-text-white/70 hover:tw-text-ink dark:hover:tw-text-white tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-font-medium">
                                <span class="material-symbols-rounded tw-text-base">info</span> ${t('terms_how_to_redeem', 'Terms & How to Redeem')}
                            </button>
                            <button type="button" class="bfwt-modal-open-report tw-text-secondary dark:tw-text-white/70 hover:tw-text-negative tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-font-medium">
                                <span class="material-symbols-rounded tw-text-base">flag</span> ${t('report_deal', 'Report Issue')}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- CARD 2: BRAND NEWSLETTER BOX -->
                <div class="tw-relative tw-w-full tw-rounded-card tw-bg-paper dark:tw-bg-[#1c1c1e] tw-border-0 tw-shadow-md tw-p-4 sm:tw-p-6 tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-3" style="border: none;">
                    <div class="tw-flex tw-items-center tw-gap-2.5">
                        ${brandLogoUrl ? `
                            <img src="${brandLogoUrl}" alt="${brandName}" class="tw-w-7 tw-h-7 tw-object-contain tw-rounded">
                        ` : `
                            <span class="material-symbols-rounded tw-text-accent tw-text-lg">mail</span>
                        `}
                        <h4 class="tw-font-heading tw-font-bold tw-text-xs sm:tw-text-sm tw-text-ink dark:tw-text-white tw-m-0">
                            Get new ${brandName ? brandName + ' ' : ''}offers straight to your inbox
                        </h4>
                    </div>

                    <form class="bfwt-modal-newsletter-form tw-w-full tw-flex tw-flex-col sm:tw-flex-row tw-gap-2">
                        <input type="email" placeholder="Enter your email address" required class="tw-w-full sm:tw-flex-1 tw-px-3.5 tw-py-2.5 tw-rounded-card tw-border tw-border-line/40 dark:tw-border-white/10 tw-bg-ink/[0.04] dark:tw-bg-white/5 tw-text-ink dark:tw-text-white tw-text-xs sm:tw-text-sm focus:tw-outline-none focus:tw-border-accent">
                        <button type="submit" class="bfwt-modal-newsletter-submit tw-flex-none tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-5 tw-py-2.5 tw-rounded-card tw-bg-accent tw-text-white tw-text-xs sm:tw-text-sm tw-font-semibold hover:tw-bg-accent-hover tw-transition-colors tw-cursor-pointer">
                            Send Me New Offers
                        </button>
                    </form>
                    <div class="bfwt-newsletter-modal-msg tw-hidden tw-text-xs tw-text-positive tw-font-semibold"></div>

                    <!-- App Download Micro Link -->
                    <div class="tw-pt-2 tw-border-t tw-border-line/15 tw-w-full tw-text-center">
                        <a href="https://budgetfitter.uk/download/" target="_blank" rel="noopener" class="tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-[0.725rem] tw-text-secondary hover:tw-text-accent tw-font-medium tw-transition-colors">
                            <span class="material-symbols-rounded tw-text-sm tw-text-accent">smartphone</span>
                            <span>Want instant code alerts? <strong class="tw-text-ink hover:tw-text-accent tw-underline">Get the BudgetFitter App →</strong></span>
                        </a>
                    </div>
                </div>

                <!-- OUTER BACKDROP FOOTER: OFFICIAL LOGO & DISCLAIMER ON TRANSPARENT BACKDROP -->
                <div class="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-2.5 tw-pt-2 tw-pb-2">
                    <div class="tw-flex tw-items-center tw-justify-center">
                        <img src="${logoWhite}" alt="BudgetFitter" class="tw-h-5 tw-w-auto tw-opacity-90">
                    </div>
                    <p class="tw-text-[0.7rem] tw-text-white/80 dark:tw-text-white/70 tw-leading-relaxed tw-m-0 tw-max-w-md">
                        ${disclaimerText}
                    </p>
                </div>

            </div>
        </div>
      `;

      const modal = this.showAndHandleModal(
        overlayHtml,
        `#coupon-popup-${dealId}`,
        `Deal - ${shortTitle}`
      );

      // Trigger rich modal open telemetry event with deterministic clickId
      if (typeof window.bfTrack === 'function') {
        window.bfTrack('deal_modal_open', {
          event_category: 'bf_deals',
          deal_id: dealId,
          deal_title: shortTitle,
          brand_name: brandName,
          brand_slug: dealData.brandSlug || dealData['brand-slug'] || '',
          is_codeless: isCodeless,
          coupon_code: promoCode,
          bf_click_id: options.bf_click_id || '',
          trigger: options.trigger || 'direct',
          placement: 'deal_companion_modal',
        });
      }

      // Copy Code Handler
      const copyBtn = modal.querySelector('.bfwt-copy-modal-code-btn');
      if (copyBtn) {
        copyBtn.addEventListener('click', async () => {
          await this.copyToClipboard(promoCode);
          copyBtn.innerHTML = '<span class="material-symbols-rounded">done</span> Copied!';
          if (typeof window.bfTrack === 'function') {
            window.bfTrack('coupon_copy', {
              event_category: 'monetization',
              coupon_code: promoCode,
              deal_id: dealId,
              deal_title: shortTitle,
              brand_name: brandName,
              brand_slug: dealData.brandSlug || dealData['brand-slug'] || '',
              bf_click_id: options.bf_click_id || '',
              placement: 'deal_modal_copy_btn',
            });
          }
          setTimeout(() => {
            copyBtn.innerHTML = '<span class="material-symbols-rounded">content_copy</span> Copy';
          }, 2000);
        });
      }

      // Feedback Vote Handlers
      const yesBtn = modal.querySelector('.bfwt-modal-vote-yes');
      const noBtn = modal.querySelector('.bfwt-modal-vote-no');
      const voteBox = modal.querySelector('.bfwt-feedback-vote-buttons');
      const thankYou = modal.querySelector('.bfwt-modal-feedback-thankyou');

      if (yesBtn) {
        yesBtn.addEventListener('click', async () => {
          if (voteBox) voteBox.style.display = 'none';
          if (thankYou) thankYou.classList.remove('tw-hidden');
          if (typeof window.bfTrack === 'function') {
            window.bfTrack('deal_feedback_vote', {
              event_category: 'engagement',
              deal_id: dealId,
              deal_title: shortTitle,
              brand_name: brandName,
              bf_click_id: options.bf_click_id || '',
              vote: 'yes',
              worked: true,
            });
          }
          await this.handleFeedbackSubmission(dealId, shortTitle, brandName, 'positive');
        });
      }

      if (noBtn) {
        noBtn.addEventListener('click', async () => {
          if (voteBox) voteBox.style.display = 'none';
          if (thankYou) thankYou.classList.remove('tw-hidden');
          if (typeof window.bfTrack === 'function') {
            window.bfTrack('deal_feedback_vote', {
              event_category: 'engagement',
              deal_id: dealId,
              deal_title: shortTitle,
              brand_name: brandName,
              vote: 'no',
              worked: false,
              reason: isCodeless ? 'Offer didn\'t work' : 'Code didn\'t work',
            });
          }
          await this.handleFeedbackSubmission(dealId, shortTitle, brandName, isCodeless ? 'Offer didn\'t work' : 'Code didn\'t work');
        });
      }

      // Terms & Report Triggers
      const infoBtn = modal.querySelector('.bfwt-modal-open-info');
      const reportBtn = modal.querySelector('.bfwt-modal-open-report');

      if (infoBtn) {
        infoBtn.addEventListener('click', () => {
          if (typeof window.bfTrack === 'function') {
            window.bfTrack('deal_terms_view', {
              event_category: 'engagement',
              deal_id: dealId,
              deal_title: shortTitle,
              brand_name: brandName,
            });
          }
          const row = document.querySelector(`.bfwt-deal-row-inner[data-dealid="${dealId}"]`);
          if (row) this.createAndShowInfoModal(row);
        });
      }

      if (reportBtn) {
        reportBtn.addEventListener('click', () => {
          const row = document.querySelector(`.bfwt-deal-row-inner[data-dealid="${dealId}"]`);
          if (row) this.createAndShowInfoModal(row, 'report');
        });
      }

      // Newsletter Form Handler
      const nlForm = modal.querySelector('.bfwt-modal-newsletter-form');
      const nlMsg = modal.querySelector('.bfwt-newsletter-modal-msg');
      if (nlForm) {
        nlForm.addEventListener('click', (e) => e.stopPropagation());
        nlForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const emailInput = nlForm.querySelector('input[type="email"]');
          const email = emailInput ? emailInput.value.trim() : '';
          if (!email) return;

          const submitBtn = nlForm.querySelector('button[type="submit"]');
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = 'Subscribing...';
          }

          try {
            const apiUrl = window.bfwtVars?.newsletterApiUrl || '/wp-json/app-api/v1/subscribe';
            const res = await fetch(apiUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': window.bfwtVars?.nonce || '',
              },
              body: JSON.stringify({ email: email }),
            });
            await res.json();
            if (nlForm) nlForm.style.display = 'none';
            if (nlMsg) {
              nlMsg.innerHTML = '✓ Thanks! We have sent a verification link to your email.';
              nlMsg.classList.remove('tw-hidden');
            }
            if (typeof window.bfTrack === 'function') {
              window.bfTrack('newsletter_signup', {
                event_category: 'conversion',
                source: 'deal_modal',
                deal_id: dealId,
                brand_name: brandName,
              });
            }
          } catch (err) {
            if (nlForm) nlForm.style.display = 'none';
            if (nlMsg) {
              nlMsg.innerHTML = '✓ Thanks for subscribing!';
              nlMsg.classList.remove('tw-hidden');
            }
          }
        });
      }
    },

    // Unified deal-info modal — Terms / How to Redeem / Help / Disclaimer / Report
    // all live as tabs in a single sheet. The report icon on a card opens the
    // same modal but jumps straight to the Report tab via initialTab='report'.
    createAndShowInfoModal(rowElement, initialTab = null) {
      const t = (k, def) => (window.bfwtTranslations && window.bfwtTranslations[k]) || def;
      const dealData = rowElement.dataset;
      const dealId = dealData.dealid;
      const shortTitle = dealData.shorttitle;
      const brandName = dealData.brandName || '';

      const termsHtml =
        rowElement.querySelector('.bfwt-hidden-terms')?.innerHTML || '';
      const hasTerms = termsHtml.trim().length > 0;
      const redeemCustomHtml = rowElement.querySelector(
        '.bfwt-hidden-redeem-custom'
      )?.innerHTML;

      const hasCode = !!dealData.promo;
      let parsedInfoModal = null;
      if (dealData.infoModal) {
        try {
          parsedInfoModal = JSON.parse(dealData.infoModal);
        } catch {
          parsedInfoModal = null;
        }
      }

      const finalRedeemHtml =
        redeemCustomHtml && redeemCustomHtml.trim().length > 0
          ? `<div class="bfwt-prose-content">${redeemCustomHtml}</div>`
          : (dealData.redeemHtmlAuto || this.buildTimelineHtml(hasCode));

      const disclosureLink =
        window.bfwtTranslations?.bf_core_disclosure_link || '/terms-and-conditions/';
      const rawDisclaimerBody =
        (parsedInfoModal && parsedInfoModal.disclaimer_text) ||
        t(
          'disclaimer_text',
          'BudgetFitter independently verifies offers against live merchant checkouts. We may earn an affiliate commission when you shop via our partner links, at zero extra cost to you. All offers remain subject to the merchant\'s official terms of service.'
        );
      const disclaimerText = `${this.escapeHtml(rawDisclaimerBody)} <a href="${disclosureLink}" target="_blank" rel="noopener" class="tw-text-accent tw-font-semibold tw-underline">${t('see_full_disclosure', 'See full disclosure')}</a>.`;

      const defaultHelpItems = [
        {
          icon: hasCode ? 'qr_code_scanner' : 'price_check',
          title: hasCode ? 'Code Formatting & Rules' : 'Markdown Already Applied',
          description: hasCode
            ? 'Use our "Copy Code" button to avoid hidden spaces. Most merchants allow one voucher code per order and exclude gift cards.'
            : 'For automatic deals and flash sales, the prices displayed on the landing page already reflect the discount.',
        },
        {
          icon: 'rule',
          title: t('help_terms_title', 'Minimum Spend & Exclusions'),
          description: t('help_terms_desc', 'Check the Terms tab for minimum basket thresholds, new-customer-only rules, or excluded clearance items.'),
        },
        {
          icon: 'vpn_lock',
          title: t('help_vpn_title', 'VPNs, Ad Blockers & Relay'),
          description: t('help_vpn_desc', 'Ad blockers or Apple iCloud Private Relay can block partner tracking links. Pause them briefly and click our button again.'),
        },
        {
          icon: 'cookie',
          title: 'Same Browser Session',
          description: 'Switching tabs, opening incognito mode, or declining merchant cookies after clicking our link can interrupt offer activation.',
        },
      ];
      const helpItems =
        parsedInfoModal && Array.isArray(parsedInfoModal.help_items) && parsedInfoModal.help_items.length > 0
          ? parsedInfoModal.help_items
          : defaultHelpItems;
      const helpIntro =
        (parsedInfoModal && parsedInfoModal.help_intro) ||
        t('help_intro', 'If your savings didn\'t apply at checkout, here are the most common causes and quick fixes:');

      const helpCardsHtml = helpItems
        .map(
          (item) => `
                        <div class="tw-flex tw-items-start tw-gap-3.5 tw-text-left tw-p-4 tw-rounded-card tw-bg-surface/50 tw-border tw-border-line/30 tw-transition-all tw-duration-300 hover:tw-border-accent/40 hover:tw-shadow-sm">
                            <div class="tw-w-9 tw-h-9 tw-rounded-full tw-bg-accent/10 tw-text-accent tw-flex tw-items-center tw-justify-center tw-shrink-0">
                                <span class="material-symbols-rounded tw-text-lg tw-leading-none">${this.escapeHtml(item.icon || 'help_outline')}</span>
                            </div>
                            <div class="tw-min-w-0">
                                <h5 class="tw-font-heading tw-font-bold tw-text-xs tw-uppercase tw-tracking-wider tw-text-ink tw-m-0 tw-mb-1">${this.escapeHtml(item.title || '')}</h5>
                                <p class="tw-text-secondary tw-text-xs tw-m-0 tw-leading-relaxed">${this.escapeHtml(item.description || '')}</p>
                            </div>
                        </div>`
        )
        .join('');

      const helpHtml = `
                <div class="tw-relative tw-overflow-hidden">
                    <p class="tw-text-secondary tw-text-sm tw-leading-relaxed tw-m-0 tw-mb-5">${this.escapeHtml(helpIntro)}</p>
                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3.5 tw-mb-6">
                        ${helpCardsHtml}
                    </div>
                    <div class="tw-mt-4 tw-text-center">
                        <a href="/contact-us/" class="${BTN_PRIMARY} tw-group tw-relative tw-overflow-hidden hover:tw-shadow-md" target="_blank" rel="noopener">
                            <span class="material-symbols-rounded tw-transition-transform tw-duration-300 group-hover:tw-rotate-12">support_agent</span>
                            <span>${t('help_contact_support', 'Contact Support')}</span>
                            <span class="material-symbols-rounded tw-transition-transform tw-duration-300 group-hover:tw-translate-x-1">arrow_forward</span>
                        </a>
                    </div>
                </div>`;

      // Tab definitions — order matters; Terms is conditional.
      const tabs = [];
      if (hasTerms)
        {tabs.push({
          id: 'terms',
          label: t('tab_terms', 'Terms'),
          icon: 'description',
          html: `<div class="bfwt-prose-content">${termsHtml}</div>`,
        });}
      tabs.push({
        id: 'redeem',
        label: t('tab_redeem', 'How to Redeem'),
        icon: 'task_alt',
        html: finalRedeemHtml,
      });
      tabs.push({
        id: 'help',
        label: t('tab_help', 'Help'),
        icon: 'help_outline',
        html: helpHtml,
      });
      tabs.push({
        id: 'disclaimer',
        label: t('tab_disclaimer', 'Disclaimer'),
        icon: 'info',
        html: `<p class="tw-text-muted tw-leading-relaxed tw-m-0">${disclaimerText}</p>`,
      });
      tabs.push({
        id: 'report',
        label: t('tab_report', 'Report'),
        icon: 'flag',
        html: this.renderFeedbackModule(dealId, shortTitle, true, brandName),
      });

      // When opened via 'report' (direct flag click on deal card), render dedicated report modal without nested accordion wrapper
      if (initialTab === 'report') {
        const reportHtml = this.renderFeedbackModule(dealId, shortTitle, true, brandName);
        const modalHtml = this.createModal({
          id: `info-popup-${dealId}`,
          className: 'bfwt-info-modal bfwt-report-direct-modal',
          title: shortTitle || t('report_deal', 'Report Deal'),
          content: `<div class="bfwt-direct-report-container tw-py-1">${reportHtml}</div>`,
          dealId: dealId,
        });

        const modal = this.showAndHandleModal(
          modalHtml,
          `#info-popup-${dealId}`,
          shortTitle,
          true
        );

        this.attachReportHandlers(modal);
        return;
      }

      // Pick initial active tab for Info Modal
      const activeTabId =
        initialTab && tabs.some((t) => t.id === initialTab)
          ? initialTab
          : tabs[0].id;

      const accordionHtml = tabs
        .map((t) => {
          const isOpen = t.id === activeTabId;
          const openAttr = isOpen ? ' open' : '';
          const isReport = t.id === 'report';

          // Canonical accordion language (aligned with AccordionWidget.php):
          // - Closed: soft satin card (tw-bg-surface) with crisp line border (tw-border-line/60), neutral icons and ink text.
          // - Open: elevates into crisp white paper (tw-bg-paper), emerald accent border & soft shadow,
          //   icon, title and chevron smoothly illuminate in accent emerald with 180deg rotation.
          const borderOpenCls = isReport
            ? '[&[open]]:tw-border-line/70 [&[open]]:tw-shadow-card'
            : '[&[open]]:tw-border-accent/40 [&[open]]:tw-shadow-card';

          const activeTextCls = isReport
            ? '[.bfwt-accordion-item[open]_&]:tw-text-negative/80'
            : '[.bfwt-accordion-item[open]_&]:tw-text-accent';

          const hoverBorderCls = isReport
            ? 'hover:tw-border-line/70'
            : 'hover:tw-border-accent/40';

          return `
            <details class="bfwt-accordion-item tw-group tw-rounded-card tw-border tw-border-line/60 tw-bg-surface tw-transition-all tw-duration-200 tw-ease-out hover:tw-border-line hover:tw-bg-surface/80 [&[open]]:tw-bg-paper ${hoverBorderCls} ${borderOpenCls} tw-mb-3" data-accordion-item name="deal-info-accordion"${openAttr}>
                <summary class="tw-w-full tw-flex tw-justify-between tw-items-center tw-gap-4 tw-py-3.5 sm:tw-py-4 tw-px-5 tw-cursor-pointer tw-list-none [&::-webkit-details-marker]:tw-hidden tw-font-heading tw-font-bold tw-tracking-[var(--ls-h5)] tw-text-sm md:tw-text-base tw-text-ink tw-leading-snug tw-transition-colors tw-duration-200 ${activeTextCls} focus-visible:tw-outline-2 focus-visible:tw-outline-accent focus-visible:tw-outline-offset-2 focus-visible:tw-rounded-card">
                    <span class="tw-flex-grow tw-flex tw-items-center tw-gap-3">
                        <span class="notranslate material-symbols-rounded tw-text-xl tw-leading-none tw-text-muted ${activeTextCls} tw-transition-colors tw-duration-200" translate="no" aria-hidden="true">${t.icon}</span>
                        <span class="${activeTextCls} tw-transition-colors tw-duration-200">${t.label}</span>
                    </span>
                    <span class="notranslate material-symbols-rounded tw-text-muted tw-text-2xl tw-shrink-0 tw-transition-transform tw-duration-300 [.bfwt-accordion-item[open]_&]:tw-rotate-180 ${activeTextCls}" translate="no" aria-hidden="true">expand_more</span>
                </summary>
                <div class="tw-px-5 tw-pb-5 tw-pt-3 tw-text-sm tw-text-secondary tw-leading-relaxed [&_p:first-child]:tw-mt-0 [&_p:last-child]:tw-mb-0 tw-border-t tw-border-line/20">
                    ${t.html}
                </div>
            </details>
          `;
        })
        .join('');

      const content = `
                <div class="bfwt-info-accordion-group tw-flex tw-flex-col">
                    ${accordionHtml}
                </div>
            `;

      const modalHtml = this.createModal({
        id: `info-popup-${dealId}`,
        className: 'bfwt-info-modal',
        title: shortTitle || t('deal_information', 'Deal Information'),
        content: content,
        dealId: dealId,
      });

      const modal = this.showAndHandleModal(
        modalHtml,
        `#info-popup-${dealId}`,
        shortTitle,
        true
      );

      // Accordion single-open logic (close other items when one is opened)
      modal
        .querySelectorAll('details[name="deal-info-accordion"]')
        .forEach((details) => {
          details.addEventListener('toggle', () => {
            if (details.open) {
              modal
                .querySelectorAll('details[name="deal-info-accordion"]')
                .forEach((other) => {
                  if (other !== details) {
                    other.removeAttribute('open');
                  }
                });
              // Auto scroll to the opened accordion item for better mobile/desktop UX
              setTimeout(() => {
                details.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                });
              }, 100);
            }
          });
        });

      // Wire up the Report tab's option pills + textarea — same logic that
      // used to live in createAndShowReportModal, scoped to the embedded module.
      this.attachReportHandlers(modal);
    },

    attachReportHandlers(modal) {
      const reportModule = modal.querySelector('.bfwt-feedback-module');
      if (!reportModule) return;

      const submitBtn = reportModule.querySelector('.bfwt-feedback-submit');
      const textAreaContainer = reportModule.querySelector(
        '.bfwt-feedback-textarea-container'
      );
      const textArea = reportModule.querySelector('textarea');

      reportModule.querySelectorAll('.bfwt-feedback-option').forEach((opt) => {
        opt.addEventListener('click', () => {
          reportModule
            .querySelectorAll('.bfwt-feedback-option')
            .forEach((o) => o.classList.remove('active'));
          opt.classList.add('active');
          const val = opt.dataset.value;
          if (val === 'Other') {
            textAreaContainer.style.display = 'block';
            if (submitBtn) submitBtn.disabled = textArea.value.trim() === '';
          } else {
            textAreaContainer.style.display = 'none';
            if (submitBtn) submitBtn.disabled = false;
          }
        });
      });

      if (textArea) {
        textArea.addEventListener('input', () => {
          const activeOpt = reportModule.querySelector(
            '.bfwt-feedback-option.active'
          );
          if (activeOpt && activeOpt.dataset.value === 'Other' && submitBtn) {
            submitBtn.disabled = textArea.value.trim() === '';
          }
        });
      }
    },

    async handleFeedbackSubmission(
      dealId,
      dealTitle,
      brandName,
      reason,
      comment = ''
    ) {
      try {
        const restUrl = window.bfwtVars?.restUrl || '/wp-json/';
        const nonce = window.bfwtVars?.nonce || '';
        const response = await fetch(
          `${restUrl}app-api/v1/report-issue`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-WP-Nonce': nonce,
            },
            body: JSON.stringify({
              deal_id: dealId,
              deal_title: dealTitle,
              brand_name: brandName,
              reason: reason,
              comment: comment,
              source: 'web',
            }),
          }
        );
        const data = await response.json();
        return data;
      } catch (error) {
        if (window.bf_debug) console.warn('Error submitting feedback:', error);
        return { success: false, message: 'Failed to submit feedback.' };
      }
    },

    renderFeedbackModule(
      dealId,
      dealTitle,
      isDirectReport = false,
      brandName = ''
    ) {
      const t = (k, def) => (window.bfwtTranslations && window.bfwtTranslations[k]) || def;
      // If direct report (Icon click), show form immediately
      const formDisplay = isDirectReport ? 'block' : 'none';
      const buttonsDisplay = isDirectReport ? 'none' : 'flex';
      const promptText = isDirectReport
        ? t('report_prompt_direct', 'What\'s the issue with this deal?')
        : t('report_prompt_vote', 'Did this deal work?');

      // Mobile App Style Reasons
      const reasons = [
        { val: 'Incorrect information', label: t('reason_incorrect', 'Incorrect information') },
        { val: 'Deal expired', label: t('reason_expired', 'Deal expired') },
        { val: 'Link not working', label: t('reason_link', 'Link not working') },
        { val: 'Code doesn\'t work', label: t('reason_code', 'Code doesn\'t work') },
        { val: 'Inappropriate content', label: t('reason_inappropriate', 'Inappropriate content') },
        { val: 'Other', label: t('reason_other', 'Other') },
      ];

      // Option pill — paper bg, line/40 border, hover accent tint, active
      // state continues from CSS (.bfwt-feedback-option.active in deals-base.css).
      const optCls = OPT_CLS;
      const radioCls = RADIO_CLS;

      let optionsHtml = '';
      if (isDirectReport) {
        optionsHtml =
          '<div class="bfwt-feedback-options-list tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2.5 tw-mb-5">';
        reasons.forEach((r) => {
          optionsHtml += `
                        <div class="${optCls}" data-value="${r.val}">
                            <span class="${radioCls}"></span>
                            <span class="bfwt-option-text">${r.label}</span>
                        </div>`;
        });
        optionsHtml += '</div>';
      }

      // Yes/No quick-feedback pills — only shown when NOT in direct-report mode.
      const quickPillCls =
        'button tw-inline-flex tw-items-center tw-gap-2.5 tw-py-2 tw-px-4 tw-border tw-border-line/40 tw-rounded-card tw-bg-paper tw-text-tertiary tw-text-xs tw-font-sans tw-font-semibold tw-tracking-[var(--ls-h5)] tw-cursor-pointer tw-transition-all tw-duration-300 tw-leading-none [&_.material-symbols-rounded]:tw-text-[1.2em]';

      return `<div class="bfwt-feedback-module tw-w-full" data-deal-id="${dealId}" data-deal-title="${dealTitle}" data-brand-name="${brandName}" data-is-direct="${isDirectReport}">
                       <div class="bfwt-feedback-prompt tw-mb-3.5 tw-font-heading tw-font-semibold tw-text-sm tw-text-secondary tw-text-left">${promptText}</div>
                       <div class="bfwt-feedback-buttons tw-justify-center tw-gap-3" style="display:${buttonsDisplay};">
                           <button class="${quickPillCls} bfwt-feedback-yes hover:tw-bg-positive hover:tw-border-positive hover:tw-text-white"><span class="material-symbols-rounded">thumb_up</span> ${t('yes', 'Yes')}</button>
                           <button class="${quickPillCls} bfwt-feedback-no hover:tw-bg-negative hover:tw-border-negative hover:tw-text-white"><span class="material-symbols-rounded">thumb_down</span> ${t('no', 'No')}</button>
                       </div>
                       <div class="bfwt-feedback-form" style="display:${formDisplay};">
                           ${optionsHtml}
                           <div class="bfwt-feedback-textarea-container tw-mb-4" style="display:none;">
                                <textarea class="tw-w-full tw-min-h-[6rem] tw-bg-paper tw-border tw-border-solid tw-border-line/40 tw-rounded-card tw-p-4 tw-text-sm tw-text-ink tw-resize-y focus:tw-border-accent focus:tw-ring-2 focus:tw-ring-accent/15 focus:tw-outline-none tw-transition-all tw-duration-200 tw-shadow-inner" placeholder="${t('report_placeholder', 'Please tell us a bit more...')}"></textarea>
                           </div>
                           <button class="${BTN_PRIMARY} bfwt-feedback-submit tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-accent" disabled>
                               <span class="material-symbols-rounded tw-text-[18px]">flag</span>
                               <span class="bfwt-btn-label">${t('submit_report', 'Submit Report')}</span>
                           </button>
                       </div>
                       <div class="bfwt-feedback-thankyou" style="display:none;"></div>
                    </div>`;
    },
  };

  // Initialize brand header watchlist button
  document.addEventListener('click', (e) => {
    const watchlistBtn = e.target.closest('.bfwt-brand-header__watchlist-icon');
    if (watchlistBtn) {
      e.preventDefault();
      const dealData = {
        dealid: watchlistBtn.dataset.dealid,
        'brand-name': watchlistBtn.dataset.brandName,
        'brand-slug': watchlistBtn.dataset.brandSlug,
      };
      window.BFWT_Popups.createAndShowWatchlistPopup(dealData);
    }
  });

  // Delegate feedback Yes/No buttons (for deal-row popups)
  document.addEventListener('click', async (e) => {
    const module = e.target.closest('.bfwt-feedback-module');
    if (!module) return;
    const isDirect = module.dataset.isDirect === 'true';
    if (isDirect) return;

    const yesBtn = e.target.closest('.bfwt-feedback-yes');
    const noBtn = e.target.closest('.bfwt-feedback-no');
    const submitBtn = e.target.closest('.bfwt-feedback-submit');
    const thankYou = module.querySelector('.bfwt-feedback-thankyou');
    const dealId = module.dataset.dealId;
    const dealTitle = module.dataset.dealTitle;
    const brandName = module.dataset.brandName;

    if (yesBtn) {
      module.querySelector('.bfwt-feedback-buttons').style.display = 'none';
      thankYou.className =
        'bfwt-feedback-thankyou tw-flex tw-items-center tw-justify-center tw-gap-3.5 tw-p-5 tw-rounded-card tw-border tw-text-center tw-bg-positive/[0.04] tw-border-positive/20 tw-text-ink';
      thankYou.innerHTML = `<span class="material-symbols-rounded tw-text-2xl tw-text-positive">check_circle</span>
         <div class="tw-text-left"><p class="tw-font-bold tw-text-sm tw-m-0">Thank You!</p><p class="tw-text-xs tw-text-secondary tw-m-0">That's great to hear — thank you!</p></div>`;
      thankYou.style.display = 'block';
      await window.BFWT_Popups.handleFeedbackSubmission(
        dealId,
        dealTitle,
        brandName,
        'positive'
      );
    }

    if (noBtn) {
      module.querySelector('.bfwt-feedback-buttons').style.display = 'none';
      const form = module.querySelector('.bfwt-feedback-form');
      form.style.display = 'block';

      const reasons = [
        'Incorrect information',
        'Deal expired',
        'Link not working',
        'Code doesn\'t work',
        'Inappropriate content',
        'Other',
      ];
      let optionsHtml =
        '<div class="bfwt-feedback-options-list tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2.5 tw-mb-5">';
      reasons.forEach((r) => {
        optionsHtml += `<div class="${OPT_CLS}" data-value="${r}"><span class="${RADIO_CLS}"></span><span class="bfwt-option-text">${r}</span></div>`;
      });
      optionsHtml += '</div>';
      form.insertAdjacentHTML('afterbegin', optionsHtml);

      const textAreaContainer = form.querySelector(
        '.bfwt-feedback-textarea-container'
      );
      const textArea = form.querySelector('textarea');
      const formSubmitBtn = form.querySelector('.bfwt-feedback-submit');

      form.querySelectorAll('.bfwt-feedback-option').forEach((opt) => {
        opt.addEventListener('click', () => {
          form
            .querySelectorAll('.bfwt-feedback-option')
            .forEach((o) => o.classList.remove('active'));
          opt.classList.add('active');
          if (opt.dataset.value === 'Other') {
            textAreaContainer.style.display = 'block';
            formSubmitBtn.disabled = textArea.value.trim() === '';
          } else {
            textAreaContainer.style.display = 'none';
            formSubmitBtn.disabled = false;
          }
        });
      });

      textArea.addEventListener('input', () => {
        const activeOpt = form.querySelector('.bfwt-feedback-option.active');
        if (activeOpt && activeOpt.dataset.value === 'Other') {
          formSubmitBtn.disabled = textArea.value.trim() === '';
        }
      });
    }

    if (submitBtn && !submitBtn.disabled) {
      const activeOpt = module.querySelector('.bfwt-feedback-option.active');
      if (!activeOpt) return;
      const reason = activeOpt.dataset.value;
      const comment =
        reason === 'Other' ? module.querySelector('textarea')?.value || '' : '';

      const btnLabel = submitBtn.querySelector('.bfwt-btn-label') || submitBtn;
      submitBtn.disabled = true;
      if (btnLabel === submitBtn) {
        submitBtn.textContent = 'Submitting...';
      } else {
        btnLabel.textContent = 'Submitting...';
        const icon = submitBtn.querySelector('.material-symbols-rounded');
        if (icon) {
          icon.textContent = 'sync';
          icon.classList.add('tw-animate-spin');
        }
      }

      const result = await window.BFWT_Popups.handleFeedbackSubmission(
        dealId,
        dealTitle,
        brandName,
        reason,
        comment
      );

      module.querySelector('.bfwt-feedback-form').style.display = 'none';
      thankYou.className =
        'bfwt-feedback-thankyou tw-flex tw-items-center tw-justify-center tw-gap-3.5 tw-p-5 tw-rounded-card tw-border tw-text-center ' +
        (result.success
          ? 'tw-bg-positive/[0.04] tw-border-positive/20 tw-text-ink'
          : 'tw-bg-negative/[0.04] tw-border-negative/20 tw-text-ink');
      thankYou.innerHTML = result.success
        ? `<span class="material-symbols-rounded tw-text-2xl tw-text-positive">check_circle</span>
           <div class="tw-text-left"><p class="tw-font-bold tw-text-sm tw-m-0">Thank You!</p><p class="tw-text-xs tw-text-secondary tw-m-0">Your report has been submitted successfully.</p></div>`
        : `<span class="material-symbols-rounded tw-text-2xl tw-text-negative">error</span>
           <div class="tw-text-left"><p class="tw-font-bold tw-text-sm tw-m-0">Submission Failed</p><p class="tw-text-xs tw-text-secondary tw-m-0">Something went wrong. Please try again later.</p></div>`;
      thankYou.style.display = 'block';
    }
  });

  // Handle direct report modal submit
  document.addEventListener('click', async (e) => {
    const module = e.target.closest('.bfwt-feedback-module');
    if (!module) return;
    const isDirect = module.dataset.isDirect === 'true';
    if (!isDirect) return;

    const submitBtn = e.target.closest('.bfwt-feedback-submit');
    if (!submitBtn || submitBtn.disabled) return;

    const activeOpt = module.querySelector('.bfwt-feedback-option.active');
    if (!activeOpt) return;

    const reason = activeOpt.dataset.value;
    const comment =
      reason === 'Other' ? module.querySelector('textarea')?.value || '' : '';
    const dealId = module.dataset.dealId;
    const dealTitle = module.dataset.dealTitle;
    const brandName = module.dataset.brandName;

    const btnLabel = submitBtn.querySelector('.bfwt-btn-label') || submitBtn;
    submitBtn.disabled = true;
    if (btnLabel === submitBtn) {
      submitBtn.textContent = 'Submitting...';
    } else {
      btnLabel.textContent = 'Submitting...';
      const icon = submitBtn.querySelector('.material-symbols-rounded');
      if (icon) {
        icon.textContent = 'sync';
        icon.classList.add('tw-animate-spin');
      }
    }

    const result = await window.BFWT_Popups.handleFeedbackSubmission(
      dealId,
      dealTitle,
      brandName,
      reason,
      comment
    );
    const thankYou = module.querySelector('.bfwt-feedback-thankyou');

    module.querySelector('.bfwt-feedback-form').style.display = 'none';
    thankYou.className =
      'bfwt-feedback-thankyou tw-flex tw-items-center tw-justify-center tw-gap-3.5 tw-p-5 tw-rounded-card tw-border tw-text-center ' +
      (result.success
        ? 'tw-bg-positive/[0.04] tw-border-positive/20 tw-text-ink'
        : 'tw-bg-negative/[0.04] tw-border-negative/20 tw-text-ink');
    thankYou.innerHTML = result.success
      ? `<span class="material-symbols-rounded tw-text-2xl tw-text-positive">check_circle</span>
         <div class="tw-text-left"><p class="tw-font-bold tw-text-sm tw-m-0">Thank You!</p><p class="tw-text-xs tw-text-secondary tw-m-0">Your report has been submitted successfully.</p></div>`
      : `<span class="material-symbols-rounded tw-text-2xl tw-text-negative">error</span>
         <div class="tw-text-left"><p class="tw-font-bold tw-text-sm tw-m-0">Submission Failed</p><p class="tw-text-xs tw-text-secondary tw-m-0">Something went wrong. Please try again later.</p></div>`;
    thankYou.style.display = 'block';
  });

  // Handle back button / pop state for all modals
  window.addEventListener('popstate', () => {
    const modal = document.querySelector('.bfwt-popup-overlay.show');
    if (modal && typeof modal.closeModal === 'function') {
      modal.closeModal();
    }
  });
})();
